import React, { useMemo,useState } from 'react';
import { toast } from 'react-toastify';
import { Close as CloseIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormHelperText,
  IconButton,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import EsimAPI from 'api/faber/EsimAPI';
import { AxiosError } from 'axios';

interface ReallocationDialogProps {
  faberToken: string;
  handleReallocationDialogClose: () => void;
  dialogOpen: boolean;
  allocationId: string | null;
}

function ReallocationDialog({
  faberToken,
  allocationId,
  handleReallocationDialogClose,
  dialogOpen,
}: ReallocationDialogProps) {
  const queryClient = useQueryClient();
  const [assigneeInput, setAssigneeInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isValidInput = useMemo(() => {
    if (!assigneeInput) {
      setError('Please enter a user ID or email');
      return false;
    }

    const userIdPrefixes = ['email|', 'google-oauth2|', 'apple|'];
    const isUserId = userIdPrefixes.some((prefix) => assigneeInput.startsWith(prefix));

    if (isUserId) {
      if (assigneeInput.length <= Math.max(...userIdPrefixes.map(p => p.length))) {
        setError('User ID must include a value after the prefix');
        return false;
      }
      setError(null);
      return true;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(assigneeInput)) {
      setError('Please enter a valid email address');
      return false;
    }

    setError(null);
    return true;
  }, [assigneeInput]);

  const { mutate, isLoading } = useMutation(
    async ({ allocationId, faberToken, assigneeEmail }: { allocationId: string; faberToken: string; assigneeEmail: string }) => {
      return EsimAPI.assignAllocation({
        allocationId,
        faberToken,
        assigneeEmail,
      });
    },
    {
      onSuccess: () => {
        toast.success('Pack reallocated successfully');
        queryClient.refetchQueries({ queryKey: ['customer-allocation-packs'] });
      },
      onError: (error: unknown) => {
        let message = 'Failed to reallocate pack';
        if (error instanceof AxiosError) {
          message = error.response?.data.error || message;
        }
        toast.error(message);
      },
      onSettled: () => {
        handleReallocationDialogClose();
        setAssigneeInput('');
        setError(null);
      },
    }
  );

  const handleReallocation = () => {
    if (!allocationId) {
      toast.error('No allocation selected');
      return;
    }
    if (!isValidInput) {
      toast.error(error || 'Invalid input');
      return;
    }
    mutate({ allocationId, faberToken, assigneeEmail: assigneeInput });
  };

  return (
    <Dialog
      open={dialogOpen}
      maxWidth="sm"
      fullWidth
      onClose={handleReallocationDialogClose}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '12px',
          p: { xs: 2, sm: 3 },
          maxWidth: { xs: '90vw', sm: '400px' },
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: { xs: '1.1rem', sm: '1.25rem' },
          color: 'text.primary',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
        }}
      >
        Reallocate Pack
        <IconButton
          onClick={handleReallocationDialogClose}
          size="small"
          sx={{ color: 'text.secondary' }}
          aria-label="Close dialog"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <TextField
          label="User ID or Email"
          fullWidth
          margin="normal"
          value={assigneeInput}
          onChange={(e) => setAssigneeInput(e.target.value)}
          error={!!error}
          helperText={error}
          sx={{ mb: 2 }}
          aria-describedby="input-helper-text"
        />
        <FormHelperText id="input-helper-text" sx={{ mt: -1, color: 'text.secondary' }}>
          Enter a user ID (e.g., email|xxx, google-oauth2|xxx, apple|xxx) or a valid email address.
        </FormHelperText>
      </DialogContent>
      <DialogActions
        sx={{
          p: { xs: 2, sm: 3 },
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-end',
        }}
      >
        <LoadingButton
          variant="text"
          onClick={handleReallocationDialogClose}
          disabled={isLoading}
        >
          Cancel
        </LoadingButton>
        <LoadingButton
          variant="contained"
          onClick={handleReallocation}
          loading={isLoading}
          loadingIndicator={<CircularProgress size={24} />}
          disabled={!isValidInput || isLoading}
          sx={{
            px: 4,
            fontWeight: 'bold',
            bgcolor: 'primary.main',
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          Confirm
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default ReallocationDialog;
